import { _analyticsQueue } from '@/utils/analytics-events';
import {
    hasAnalyticsConsent,
    onAnalyticsConsentChange,
    isNewPageview,
    sanitizeEventUrls,
    stripSensitiveParams,
} from '@/utils/consent';

// PostHog only runs with CookieYes analytics consent.
//
// Before consent nothing is imported, initialised, stored or sent: posthog-js
// is not even fetched. Consent comes from CookieYes (loaded by GTM), and Global
// Privacy Control counts as a refusal — see utils/consent.ts. Events tracked
// through useAnalytics() before consent are dropped there, not queued.
//
// As a second lock, PostHog is initialised opted OUT with persistence disabled
// (opt_out_capturing_by_default + opt_out_persistence_by_default), then opted
// in explicitly. If consent is withdrawn mid-session, opt_out_capturing()
// stops capture and deletes PostHog's cookie/localStorage, and
// clear_opt_in_out_capturing() removes the opt-out marker too, leaving nothing
// behind. On the next visit without consent PostHog is never loaded.
//
// The `posthogHost` runtime setting is intentionally not read: events go
// through the first-party relay at /relay-ohc, which hardcodes the US host.
export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig();
    const key = config.public.posthogKey;
    if (!key) return; // no key (dev default) → analytics fully disabled

    // Resolved now, inside the plugin's Nuxt context, not after an await.
    const router = useRouter();
    const { user } = useAuth();

    let posthog: typeof import('posthog-js').default | null = null;
    let starting = false;
    let consented = false;
    let identifiedAs: string | null = null;

    const capturePageview = () => {
        posthog?.capture('$pageview', { $current_url: stripSensitiveParams(window.location.href) });
    };

    // Identify by the opaque user id only — no email, no name.
    const syncIdentity = () => {
        if (!posthog || !consented) return;
        const id = user.value?.id ?? null;
        if (id && id !== identifiedAs) {
            identifiedAs = id;
            posthog.identify(id);
        } else if (!id && identifiedAs) {
            identifiedAs = null;
            // reset() returns PostHog to its default consent state, which is
            // opted out, so opt back in after it — never before.
            posthog.reset();
            posthog.opt_in_capturing({ captureEventName: false });
        }
    };

    const grant = () => {
        if (!posthog) return;
        posthog.opt_in_capturing({ captureEventName: false });
        capturePageview(); // the page the visitor is on now
        syncIdentity();
    };

    const revoke = () => {
        if (!posthog) return;
        posthog.opt_out_capturing();          // stop capture, delete PostHog storage
        posthog.clear_opt_in_out_capturing(); // and the opt-out marker; default stays opted out
        identifiedAs = null;
    };

    const start = async () => {
        try {
            // Dynamic import: posthog-js must NOT land in the eagerly-loaded
            // hydration entry chunk. Importing here makes it its own chunk,
            // fetched only once the visitor has consented and the idle
            // callback has fired.
            const { default: ph } = await import('posthog-js');

            ph.init(key, {
                api_host: '/relay-ohc',
                ui_host: 'https://us.posthog.com',
                person_profiles: 'identified_only',
                capture_pageview: false,          // SPA pageviews are manual below
                autocapture: true,
                disable_session_recording: true,  // spec: no replay
                advanced_disable_flags: true,     // spec: no feature flags; skips the flags/decide call on init
                                                   // (installed posthog-js@1.416.0 uses this name; older versions
                                                   // named it `advanced_disable_decide` — verify against
                                                   // node_modules/posthog-js/dist/module.d.ts if upgrading)
                opt_out_capturing_by_default: true,
                opt_out_persistence_by_default: true,
                // Defence in depth: no credential (magic-link ?token=) leaves
                // the browser in a URL property, whatever captured it.
                before_send: (event) => sanitizeEventUrls(event),
            });
            posthog = ph;
            _analyticsQueue.setReady((event, props) => ph.capture(event, props));

            // SPA pageviews: only when the path changes. Itinerary pages
            // router.replace() their query string on every edit.
            router.afterEach((to, from) => {
                if (consented && isNewPageview(to.path, from?.path)) capturePageview();
            });

            // useAuth().user is kept in sync by composables/auth.ts on every
            // better-auth sign-in/sign-out.
            watch(user, syncIdentity);

            // Consent may have been withdrawn while the chunk was loading.
            if (consented) grant();
            else revoke();
        } catch (error) {
            console.error('Error loading PostHog:', error); // analytics must never break the page
        }
    };

    const apply = (next: boolean) => {
        if (next === consented) return;
        consented = next;
        if (!next) return revoke();
        if (posthog) return grant(); // granted again after a withdrawal this session
        if (starting) return;
        starting = true;
        // Defer past hydration: idle callback with 1s fallback.
        if ('requestIdleCallback' in window) requestIdleCallback(() => start(), { timeout: 3000 });
        else setTimeout(start, 1000);
    };

    // PWA installs (Chromium fires `appinstalled`; Safari has no equivalent).
    // Pushed through the same consent-checked queue as useAnalytics(): held
    // until PostHog is ready, dropped without consent. Not in AnalyticsEvents
    // because that type lives in utils/analytics-events.ts.
    window.addEventListener('appinstalled', () => {
        if (hasAnalyticsConsent()) _analyticsQueue.push('app_installed', {});
    });

    // Register before GTM has loaded CookieYes, so its events are never missed.
    onAnalyticsConsentChange(apply);
    apply(hasAnalyticsConsent());
});
